import { useEffect, useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import {
  useJadwal, useSimpanJadwal, useSimpanTemplate, useTemplate,
} from '../../../domains/tenancy/api'
import {
  AreaTeks, Bendera, Body, Gagal, Isian, Memuat, Panel, Pilihan, Seg, Sub, Tombol,
  useSimpanKepala,
} from '../../../shared/ui'

// ISO: 1 Senin … 7 Minggu. Sama dengan `date.isoweekday()` di backend (ADR-061) — peta
// konversi yang meleset satu hari adalah cacat yang tidak menimbulkan galat, ia cuma
// menawarkan hari yang salah.
const HARI = [
  [1, 'Sen'], [2, 'Sel'], [3, 'Rab'], [4, 'Kam'], [5, 'Jum'], [6, 'Sab'], [7, 'Min'],
]

/**
 * Jadwal satu kelompok fitur. Dipakai tab "Jadwal balas" (chatbot) dan tab jadwal di
 * layar Meeting dan Konten — satu komponen, karena ketiganya tabel dan aturan yang sama.
 */
/**
 * DI LUAR JAM ITU, BOT MELAKUKAN APA? — panel kedua tab "Jadwal balas" di desain.
 *
 * Ini pilihan yang paling sering dilewatkan dan paling terasa akibatnya: "diam saja"
 * berarti orang mengirim DM jam 9 malam dan tidak mendapat apa-apa — dari sisi mereka,
 * itu tidak bisa dibedakan dari diabaikan.
 *
 * Percakapan TETAP masuk antrean tim apa pun pilihannya; yang berbeda cuma apakah
 * pengirimnya tahu pesannya sampai.
 *
 * "Tetap balas penuh" yang ada di mockup TIDAK dibangun, dan sengaja tidak ditampilkan
 * sebagai tombol mati: perilakunya bukan setelan template melainkan mengabaikan jadwal
 * sama sekali — yaitu mengosongkan jadwalnya, yang sudah bisa dilakukan di panel atas.
 * Menaruhnya di sini akan membuat dua tempat mengatur satu hal.
 */
// Diekspor untuk `uji_render.jsx`: komponen induknya menahan render di balik state
// yang diisi useEffect, dan efek tidak berjalan di renderToString.
export function DiLuarJam({ t, berlaku = true }) {
  const KUNCI = 'chatbot_di_luar_jam'
  const q = useTemplate(KUNCI)
  const simpan = useSimpanTemplate(KUNCI)
  const [isi, setIsi] = useState('')
  const [aktif, setAktif] = useState(false)

  useEffect(() => {
    if (!q.data) return
    setIsi(q.data.isi || '')
    setAktif(Boolean(q.data.aktif))
  }, [q.data])

  useSimpanKepala(() => simpan.mutate({ isi, aktif }),
                  { menyimpan: simpan.isPending })

  return (
    <Panel judul={t('ta.luarJamJudul')}>
      <Body className="flex flex-col gap-3 max-w-[80ch]">
        {q.isLoading && <Memuat tinggi={3} />}
        {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

        {/* MODE "SELALU" TIDAK PUNYA LUAR JAM, dan panel ini harus mengatakannya.
            Bentuk sebelumnya menampilkannya apa adanya: pilihan tetap bisa diubah,
            teks tetap bisa disimpan, dan tidak satu pun akan pernah terkirim —
            `sedang_terjadwal` selalu true, jadi `chatbot_di_luar_jam()` tidak pernah
            dipanggil. Setelan yang tersimpan rapi dan tidak berlaku lebih buruk
            daripada setelan yang tidak ada: yang pertama membuat orang berhenti
            mencari sebab bot diam.

            DITAMPILKAN, BUKAN DISEMBUNYIKAN. Nilainya tetap tersimpan dan berlaku lagi
            begitu jadwalnya kembali mingguan; menyembunyikannya membuat teks yang
            pernah ditulis seolah hilang. */}
        {q.data && !berlaku && (
          <Bendera nada="info">{t('ta.luarJamTakBerlaku')}</Bendera>
        )}

        {q.data && (
          /* SATU `<fieldset disabled>` UNTUK SELURUH KENDALI, bukan `disabled` yang
             ditempel satu per satu. Peramban mematikan semua yang di dalamnya secara
             bawaan — termasuk kotak teks yang di SSR belum dirender — jadi tidak ada
             kendali yang bisa tertinggal hidup karena lupa ditempeli.
             `display: contents` membuat fieldset-nya tidak mengubah tata letak. */
          <fieldset disabled={!berlaku} className="contents border-0 p-0 m-0">
            {/* DIMATIKAN, bukan sekadar diabaikan: tombol yang terlihat hidup lalu tidak
                bereaksi terbaca sebagai layar rusak, bukan sebagai setelan yang memang
                tidak berlaku. */}
            <Seg
              nilai={aktif ? 'balas' : 'diam'}
              set={(v) => setAktif(v === 'balas')}
              daftar={[
                { id: 'diam', label: t('ta.luarJamDiam') },
                { id: 'balas', label: t('ta.luarJamBalas') },
              ]}
            />

            {aktif && (
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-ink3">
                  {t('ta.luarJamTeks')}
                </span>
                <AreaTeks
                  value={isi}
                  baris={3}
                  placeholder="Terima kasih! Tim kami akan menghubungi Anda besok pagi."
                  onChange={(e) => setIsi(e.target.value)}
                />
              </label>
            )}

            <p className="m-0 text-[12px] text-ink2">{t('ta.luarJamCatatan')}</p>
            {!q.data.milik_tenant && <Sub>{t('ta.luarJamBawaan')}</Sub>}

            {simpan.isError && (
              <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>
            )}

          </fieldset>
        )}
      </Body>
    </Panel>
  )
}

export default function JadwalBalas({ kunci }) {
  const { t } = useBahasa()
  const q = useJadwal()
  const simpan = useSimpanJadwal()
  // SUNTINGAN ORANGNYA saja yang jadi state; nilai awalnya DITURUNKAN saat render.
  //
  // Bentuk sebelumnya mengisi seluruh formulir lewat `useEffect`, dan itu punya satu
  // akibat yang tidak terlihat di layar sama sekali: `renderToString` tidak menjalankan
  // efek (XXIII-1), jadi komponen ini SELALU berhenti di `<Memuat>` dan seluruh badannya
  // tidak pernah teruji. Terbukti kontrol negatif 13 September — empat suntikan di
  // bagian ini lolos semua, termasuk yang mengembalikan cacat aslinya.
  //
  // Menurunkannya saat render juga menghapus satu kedipan: sebelumnya layar menampilkan
  // "memuat" satu frame lagi SESUDAH datanya tiba, menunggu efeknya jalan.
  const [ubahan, setUbahan] = useState(null)

  const baris = (q.data ?? []).find((x) => x.feature_key === kunci)
  const f = ubahan ?? (baris ? {
    mode: baris.mode,
    hari: baris.hari?.length ? baris.hari : [1, 2, 3, 4, 5],
    mulai: baris.mulai || '08:00',
    selesai: baris.selesai || '17:00',
  } : null)
  const setF = setUbahan

  // DI ATAS SEMUA `return` AWAL — lihat catatan yang sama di `Persona.jsx`. `f` masih
  // null selama memuat, dan itu tidak apa-apa: fungsinya dibaca ulang saat tombol
  // ditekan, dan tombolnya mati selama belum ada formulir yang siap.
  useSimpanKepala(() => f && simpan.mutate({ key: kunci, ...f }),
                  { bisa: Boolean(f), menyimpan: simpan.isPending })

  if (q.isLoading || !f) return <Memuat tinggi={4} />
  if (q.isError) return <Gagal galat={q.error} ulangi={q.refetch} />

  const mingguan = f.mode === 'weekly'

  return (
    <div className="flex flex-col gap-3.5">
    <Panel judul={t(`ta.jadwal.${kunci}`)} catatan={t(`ta.jadwalCatatan.${kunci}`)}>
      <div className="p-3.5 flex flex-col gap-3 max-w-[64ch]">
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
            {t('ta.jadwalMode')}
          </span>
          <Pilihan value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
            <option value="always">{t('ta.modeAlways')}</option>
            <option value="weekly">{t('ta.modeWeekly')}</option>
          </Pilihan>
        </label>

        {mingguan && (
          <>
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                {t('ta.jadwalHari')}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {HARI.map(([n, label]) => {
                  const aktif = f.hari.includes(n)
                  return (
                    <Tombol
                      key={n}
                      kecil
                      jenis={aktif ? 'utama' : 'sekunder'}
                      onClick={() => setF({
                        ...f,
                        hari: aktif ? f.hari.filter((x) => x !== n) : [...f.hari, n].sort(),
                      })}
                    >
                      {label}
                    </Tombol>
                  )
                })}
              </div>
              {/* Daftar hari KOSONG berarti "tidak pernah", bukan "hari kerja"
                  (ADR-062). Dinyatakan supaya tidak ada yang mengosongkannya sambil
                  mengira itu mengembalikan ke bawaan. */}
              {f.hari.length === 0 && (
                <span className="text-[11.5px] text-warn">{t('ta.hariKosong')}</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {[['mulai', 'ta.jadwalMulai'], ['selesai', 'ta.jadwalSelesai']].map(([k, lbl]) => (
                <label key={k} className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                    {t(lbl)}
                  </span>
                  <Isian
                    type="time"
                    value={f[k]}
                    onChange={(e) => setF({ ...f, [k]: e.target.value })}
                  />
                </label>
              ))}
            </div>
          </>
        )}

        {simpan.isError && (
          <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>
        )}

      </div>
    </Panel>
      {/* Hanya chatbot yang punya perilaku "di luar jam" — Meeting dan Konten memakai
          komponen yang sama untuk jadwalnya, dan tidak ada teks yang dikirim ke siapa
          pun saat keduanya di luar jam.

          `berlaku` mengikuti MODENYA: "Selalu" berarti tidak ada luar jam sama sekali. */}
      {kunci === 'chatbot' && <DiLuarJam t={t} berlaku={mingguan} />}
    </div>
  )
}
